import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  ArrowBack,
  DeleteOutline,
  EditOutlined,
  FileCopyOutlined,
  Link as LinkIcon,
} from '@material-ui/icons';
import { toast } from 'react-toastify';
import NewsletterData from 'services/dataService/NewsletterData';
import { NEWSLETTER_STATUS, getNewsletterLink } from 'helpers/newsletter';
import { Post } from '../components';
import { useStyles } from './styles';

export default () => {
  const { id } = useParams();
  const history = useHistory();
  const classes = useStyles();
  const { t } = useTranslation(['newsletter', 'button', 'toastify']);

  const [newsletter, setNewsletter] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [linksOpen, setLinksOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await NewsletterData.getNewsletterById(id);
        setNewsletter(response.data);
      } catch (err) {
        history.push('/dashboard');
        if (err.response?.status === 404) {
          toast.error(t('newsletter:toast.not_found'));
        } else if (err.response?.status === 403) {
          toast.error(t('toastify:commons.toast_error_403'));
        } else {
          toast.error(t('toastify:commons.toast_error_api'));
        }
      }
    };

    fetchData();
  }, [id]);

  const handleCopyLink = async (domain) => {
    const link = getNewsletterLink(id, domain);

    try {
      await navigator.clipboard.writeText(link);
      toast.success(t('newsletter:toast.link_copied'));
    } catch (err) {
      window.prompt(t('newsletter:view.copy_link'), link);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await NewsletterData.deleteNewsletter(id);
      toast.success(t('newsletter:toast.deleted'));
      history.push('/dashboard');
    } catch (err) {
      toast.error(t('toastify:commons.toast_error_api_remove'));
      setDeleting(false);
    }
  };

  if (!newsletter) return null;

  const isDraft = newsletter.status === NEWSLETTER_STATUS.draft;

  return (
    <div className={classes.root}>
      <div className={classes.toolbar}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => history.push('/dashboard')}
        >
          {t('newsletter:view.back')}
        </Button>
        <div className={classes.toolbarActions}>
          {!isDraft && (
            <Button
              color="primary"
              variant="outlined"
              startIcon={<LinkIcon />}
              onClick={() =>
                newsletter.can_edit ? setLinksOpen(true) : handleCopyLink()
              }
            >
              {t('newsletter:view.copy_link')}
            </Button>
          )}
          {newsletter.can_edit && (
            <>
              <Button
                color="primary"
                variant="outlined"
                startIcon={<EditOutlined />}
                onClick={() => history.push(`/newsletter/${id}/edit`)}
              >
                {t('newsletter:view.edit')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<DeleteOutline />}
                onClick={() => setDeleteOpen(true)}
              >
                {t('newsletter:view.delete')}
              </Button>
            </>
          )}
        </div>
      </div>

      {newsletter.can_edit && (
        <div className={classes.shared}>
          {isDraft ? (
            <Typography variant="body2" color="textSecondary">
              {t('newsletter:view.draft_notice')}
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="textSecondary">
                {t('newsletter:view.shared_with')}
              </Typography>
              {newsletter.companies.map((company) => (
                <Chip key={company.id} size="small" label={company.name} />
              ))}
            </>
          )}
        </div>
      )}

      <Card>
        <CardContent className={classes.content}>
          <Post
            title={newsletter.title}
            coverUrl={newsletter.cover?.url}
            content={newsletter.content}
            publishedAt={newsletter.published_at}
          />
        </CardContent>
      </Card>

      {newsletter.can_edit && (
        <Dialog
          fullWidth
          maxWidth="sm"
          open={linksOpen}
          onClose={() => setLinksOpen(false)}
        >
          <DialogTitle>{t('newsletter:links.title')}</DialogTitle>
          <DialogContent dividers>
            <DialogContentText>
              {t('newsletter:links.description')}
            </DialogContentText>
            <List dense disablePadding>
              {newsletter.companies.map((company) => (
                <ListItem key={company.id} divider disableGutters>
                  <ListItemText
                    primary={company.name}
                    secondary={getNewsletterLink(id, company.domain)}
                    secondaryTypographyProps={{ noWrap: true }}
                    className={classes.linkText}
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title={t('newsletter:view.copy_link')}>
                      <IconButton
                        edge="end"
                        aria-label={`${t('newsletter:view.copy_link')}: ${
                          company.name
                        }`}
                        onClick={() => handleCopyLink(company.domain)}
                      >
                        <FileCopyOutlined />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLinksOpen(false)}>
              {t('newsletter:links.close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>{t('newsletter:view.delete_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('newsletter:view.delete_text')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={deleting} onClick={() => setDeleteOpen(false)}>
            {t('button:commons.btn_cancel')}
          </Button>
          <Button color="primary" disabled={deleting} onClick={handleDelete}>
            {t('newsletter:view.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
